import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import StaffRoute from './components/auth/StaffRoute';
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import JobDetailPage from './pages/jobs/JobDetailPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ApplicationsPage from './pages/applications/ApplicationsPage';
import ApplicationDetailPage from './pages/applications/ApplicationDetailPage';
import ProfilePage from './pages/profile/ProfilePage';
import AnswerLibraryPage from './pages/answers/AnswerLibraryPage';
import ResumesPage from './pages/resumes/ResumesPage';
import EmailTrackingPage from './pages/tracking/EmailTrackingPage';
import AdminPage from './pages/admin/AdminPage';
import CostsPage from './pages/admin/CostsPage';
import KeysPage from './pages/admin/KeysPage';
import PromptsPage from './pages/admin/PromptsPage';
import PlansPage from './pages/admin/PlansPage';
import TermsPage from './pages/legal/TermsPage';
import PlanPage from './pages/account/PlanPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import GuidePage from './pages/help/GuidePage';
import JobSourcesPage from './pages/admin/JobSourcesPage';
import NotFoundPage from './pages/NotFoundPage';
import { useAuthStore } from './store/authStore';

/** `/jobs` is the board at `/`; keep the search that came with the link. */
function JobsRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search }} replace />;
}

function App() {
  const loadUser = useAuthStore((state) => state.loadUser);

  // Re-validate the persisted session against the server on load. If the token
  // has been revoked or expired, this drops the app to a clean logged-out state.
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Routes with layout */}
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="jobs" element={<JobsRedirect />} />
          <Route path="jobs/:id" element={<JobDetailPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="help" element={<GuidePage />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="applications" element={<ApplicationsPage />} />
            <Route path="applications/:id" element={<ApplicationDetailPage />} />
            <Route path="answers" element={<AnswerLibraryPage />} />
            <Route path="resumes" element={<ResumesPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="plan" element={<PlanPage />} />
            <Route path="email-tracking" element={<EmailTrackingPage />} />
          </Route>

          {/* Staff only */}
          <Route element={<StaffRoute />}>
            <Route path="admin" element={<AdminPage />}>
              <Route index element={<Navigate to="/admin/jobs" replace />} />
              <Route path="jobs/:tab?" element={<JobSourcesPage />} />
              <Route path="costs/:tab?" element={<CostsPage />} />
              <Route path="keys" element={<KeysPage />} />
              <Route path="prompts" element={<PromptsPage />} />
              <Route path="plans" element={<PlansPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Box>
  );
}

export default App;
